// Doctor service for managing doctor profiles
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { getEffectiveAccountId } from './authorizationService';

class DoctorService {
  constructor() {
    this.collectionName = 'doctors';
    this.storage = storage;
    this.maxFileSize = 10 * 1024 * 1024; // 10MB
  }

  // Validate file before upload
  validateFile(file) {
    const errors = [];

    if (file.size > this.maxFileSize) {
      errors.push(`File size must be less than ${this.maxFileSize / (1024 * 1024)}MB`);
    }

    if (!file.type.startsWith('image/')) {
      errors.push('Only image files are allowed for profile photos');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Upload profile photo
  async uploadProfilePhoto(file, doctorId, accountId) {
    try {
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `accounts/${accountId}/doctors/${doctorId}/${fileName}`;
      const storageRef = ref(this.storage, filePath);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        url: downloadURL,
        path: filePath
      };
    } catch (error) {
      console.error('Profile photo upload error:', error);
      throw error;
    }
  }

  // Create a new doctor
  async createDoctor(doctorData, accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        throw new Error('No account scope set. Please select an account.');
      }

      let profilePhotoUrl = '';

      // Filter out undefined values to prevent Firebase errors
      const filteredDoctorData = Object.fromEntries(
        Object.entries(doctorData).map(([key, value]) => {
          // Handle arrays that might contain undefined values
          if (Array.isArray(value)) {
            const filteredArray = value.filter(item => item !== undefined);
            return [key, filteredArray];
          }
          // Keep all defined values, including empty strings and null
          // Only exclude undefined values
          return value !== undefined ? [key, value] : null;
        }).filter(Boolean)
      );

      // Handle profile photo upload if present
      if (doctorData.profilePhoto && doctorData.profilePhoto instanceof File) {
        // First create the doctor document to get an ID
        const tempDoctor = {
          ...filteredDoctorData,
          profilePhoto: '', // Temporarily empty
          accountId: effectiveAccountId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        const docRef = await addDoc(collection(db, this.collectionName), tempDoctor);
        const doctorId = docRef.id;
        
        // Upload profile photo
        const photoResult = await this.uploadProfilePhoto(doctorData.profilePhoto, doctorId, effectiveAccountId);
        profilePhotoUrl = photoResult.url;
        
        // Update the doctor with the photo URL
        const finalDoctor = {
          ...filteredDoctorData,
          profilePhoto: profilePhotoUrl,
          accountId: effectiveAccountId,
          updatedAt: new Date().toISOString()
        };
        
        await updateDoc(docRef, finalDoctor);
        
        return { id: doctorId, ...finalDoctor };
      } else {
        // No profile photo to upload
        const doctorWithTimestamp = {
          ...filteredDoctorData,
          profilePhoto: '',
          accountId: effectiveAccountId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        const docRef = await addDoc(collection(db, this.collectionName), doctorWithTimestamp);
        
        return { id: docRef.id, ...doctorWithTimestamp };
      }
    } catch (error) {
      console.error('Error creating doctor:', error);
      throw error;
    }
  }

  // Get all doctors for an account
  async getDoctors(accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        console.log('No effective account ID, cannot load doctors');
        return [];
      }

      const q = query(
        collection(db, this.collectionName),
        where('accountId', '==', effectiveAccountId)
      );

      const querySnapshot = await getDocs(q);
      const doctors = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        doctors.push({
          id: doc.id,
          ...data
        });
      });

      // Sort by name
      doctors.sort((a, b) => a.name.localeCompare(b.name));

      return doctors;
    } catch (error) {
      console.error('Error getting doctors:', error);
      return [];
    }
  }

  // Update a doctor
  async updateDoctor(doctorId, doctorData, accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        throw new Error('No account scope set. Please select an account.');
      }

      console.log('updateDoctor - Original doctorData:', doctorData);
      let finalDoctorData = { ...doctorData };

      // Handle profile photo upload if present
      if (doctorData.profilePhoto && doctorData.profilePhoto instanceof File) {
        const photoResult = await this.uploadProfilePhoto(doctorData.profilePhoto, doctorId, effectiveAccountId);
        finalDoctorData.profilePhoto = photoResult.url;
      } else if (doctorData.profilePhoto === null) {
        // Explicitly set to empty string to remove existing photo
        finalDoctorData.profilePhoto = '';
      }
      // If doctorData.profilePhoto is a string (existing URL), keep it as is

      // Filter out undefined values to prevent Firebase errors
      console.log('updateDoctor - Before filtering finalDoctorData:', finalDoctorData);
      
      const filteredData = Object.fromEntries(
        Object.entries(finalDoctorData).map(([key, value]) => {
          // Handle arrays that might contain undefined values
          if (Array.isArray(value)) {
            const filteredArray = value.filter(item => item !== undefined);
            return [key, filteredArray];
          }
          // Keep all defined values, including empty strings and null
          // Only exclude undefined values
          const isDefined = value !== undefined;
          if (!isDefined) {
            console.log(`updateDoctor - Filtering out undefined field: ${key} = ${value}`);
          }
          return isDefined ? [key, value] : null;
        }).filter(Boolean)
      );

      console.log('updateDoctor - After filtering filteredData:', filteredData);
      const updateData = {
        ...filteredData,
        updatedAt: new Date().toISOString()
      };

      console.log('updateDoctor - Final updateData:', updateData);
      const doctorRef = doc(db, this.collectionName, doctorId);
      await updateDoc(doctorRef, updateData);

      return { id: doctorId, ...updateData };
    } catch (error) {
      console.error('Error updating doctor:', error);
      throw error;
    }
  }

  // Delete a doctor
  async deleteDoctor(doctorId) {
    try {
      // Get doctor data first to check for profile photo
      const doctorRef = doc(db, this.collectionName, doctorId);
      const doctorDoc = await getDocs(query(collection(db, this.collectionName), where('__name__', '==', doctorId)));
      
      if (!doctorDoc.empty) {
        const doctorData = doctorDoc.docs[0].data();
        
        // Delete profile photo from storage if it exists
        if (doctorData.profilePhoto && doctorData.profilePhoto.includes('firebasestorage')) {
          try {
            // Extract path from URL
            const url = new URL(doctorData.profilePhoto);
            const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
            if (pathMatch) {
              const filePath = decodeURIComponent(pathMatch[1]);
              const storageRef = ref(this.storage, filePath);
              await deleteObject(storageRef);
            }
          } catch (storageError) {
            console.warn('Could not delete profile photo from storage:', storageError);
          }
        }
      }

      // Delete doctor document
      await deleteDoc(doctorRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting doctor:', error);
      throw error;
    }
  }

  // Get doctors by specialty
  async getDoctorsBySpecialty(specialty, accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        return [];
      }

      const q = query(
        collection(db, this.collectionName),
        where('accountId', '==', effectiveAccountId),
        where('specialty', '==', specialty)
      );

      const querySnapshot = await getDocs(q);
      const doctors = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        doctors.push({
          id: doc.id,
          ...data
        });
      });

      return doctors;
    } catch (error) {
      console.error('Error getting doctors by specialty:', error);
      return [];
    }
  }

  // Get doctors by location
  async getDoctorsByLocation(locationId, accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        return [];
      }

      const q = query(
        collection(db, this.collectionName),
        where('accountId', '==', effectiveAccountId),
        where('locationId', '==', locationId)
      );

      const querySnapshot = await getDocs(q);
      const doctors = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        doctors.push({
          id: doc.id,
          ...data
        });
      });

      return doctors;
    } catch (error) {
      console.error('Error getting doctors by location:', error);
      return [];
    }
  }
}

const doctorService = new DoctorService();
export default doctorService;
